<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useWsStore, type WsEvent } from '../stores/ws'
import { getAllInstances } from '../lib/api'
import type { Instance } from '../lib/types'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Tag from 'primevue/tag'

const wsStore = useWsStore()

const open = ref(false)
const filterText = ref('')
const selectedEvent = ref<WsEvent | null>(null)
const expanded = ref(false) // half vs full height
const inspectorInstances = ref<Instance[]>([])

const EVENT_COLORS: Record<string, string> = {
  MESSAGE: 'success',
  SEND_MESSAGE: 'info',
  CONNECTION: 'warn',
  QRCODE: 'warn',
  CALL: 'danger',
  READ_RECEIPT: 'secondary',
  CHAT_PRESENCE: 'secondary',
  PRESENCE: 'secondary',
  LABEL: 'info',
  GROUP: 'info',
  CONTACT: 'info',
  HISTORY_SYNC: 'secondary',
  NEWSLETTER: 'info',
  UNKNOWN: 'secondary',
}

function severityFor(event: string): string {
  return EVENT_COLORS[event] ?? 'secondary'
}

const filteredEvents = computed(() => {
  const q = filterText.value.trim().toLowerCase()
  if (!q) return wsStore.events
  return wsStore.events.filter(
    (e) =>
      e.event.toLowerCase().includes(q) ||
      e.instanceId.toLowerCase().includes(q) ||
      JSON.stringify(e.data).toLowerCase().includes(q),
  )
})

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour12: false })
}

function payloadSize(data: unknown): string {
  const bytes = new TextEncoder().encode(JSON.stringify(data)).length
  return bytes < 1024 ? `${bytes}B` : `${(bytes / 1024).toFixed(1)}KB`
}

function exportJsonl() {
  const lines = wsStore.events.map((e) => JSON.stringify(e)).join('\n')
  const blob = new Blob([lines], { type: 'application/jsonl' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `neogo-events-${Date.now()}.jsonl`
  a.click()
  URL.revokeObjectURL(url)
}

async function ensureInstanceToken(): Promise<{ token: string; id: string } | null> {
  if (inspectorInstances.value.length === 0) {
    const res = await getAllInstances()
    inspectorInstances.value = res.data?.data || res.data || []
  }
  const instance = inspectorInstances.value.find((item) => item.token)
  if (!instance) return null
  return { token: instance.token, id: instance.id }
}

async function toggle() {
  open.value = !open.value
  if (open.value && !wsStore.connected) {
    const instance = await ensureInstanceToken()
    if (instance) {
      wsStore.connect(instance.token, instance.id)
    }
  }
}

// Keyboard shortcut Cmd/Ctrl+Shift+E
function onKeydown(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'E') {
    e.preventDefault()
    void toggle()
  }
}
function copyEvent() {
  if (selectedEvent.value) navigator.clipboard.writeText(JSON.stringify(selectedEvent.value.data, null, 2))
}
onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => window.removeEventListener('keydown', onKeydown))
</script>

<template>
  <!-- Trigger button -->
  <div class="mfi-trigger" @click="toggle" :title="open ? 'Fechar Inspector (⌘⇧E)' : 'Abrir Inspector (⌘⇧E)'">
    <span class="mfi-trigger-dot" :class="{ 'mfi-trigger-dot--live': wsStore.connected && !wsStore.paused }" />
    <i class="pi pi-code" />
    <span class="mfi-trigger-label">Flow</span>
  </div>

  <!-- Panel -->
  <Transition name="mfi-slide">
    <div
      v-if="open"
      class="mfi-panel"
      :class="{ 'mfi-panel--expanded': expanded }"
    >
      <!-- Header -->
      <div class="mfi-header">
        <div class="mfi-header-left">
          <span class="mfi-title">
            <i class="pi pi-code" />
            Message Flow Inspector
          </span>
          <span class="mfi-status" :class="{ 'mfi-status--live': wsStore.connected }">
            {{ wsStore.connected ? '● LIVE' : '○ OFF' }}
          </span>
        </div>
        <div class="mfi-header-right">
          <InputText
            v-model="filterText"
            placeholder="Filtrar (event, instance, payload)..."
            size="small"
            class="mfi-filter"
          />
          <Button
            :icon="wsStore.paused ? 'pi pi-play' : 'pi pi-pause'"
            :label="wsStore.paused ? 'Retomar' : 'Pausar'"
            size="small"
            text
            @click="wsStore.paused = !wsStore.paused"
          />
          <Button
            icon="pi pi-download"
            label="Export"
            size="small"
            text
            @click="exportJsonl"
          />
          <Button
            icon="pi pi-trash"
            size="small"
            text
            severity="danger"
            @click="wsStore.clear(); selectedEvent = null"
          />
          <Button
            :icon="expanded ? 'pi pi-window-minimize' : 'pi pi-window-maximize'"
            size="small"
            text
            @click="expanded = !expanded"
          />
          <Button
            icon="pi pi-times"
            size="small"
            text
            @click="open = false"
          />
        </div>
      </div>

      <!-- Body -->
      <div class="mfi-body">
        <!-- Event table -->
        <div class="mfi-table-wrap">
          <div v-if="filteredEvents.length === 0" class="mfi-empty">
            <i class="pi pi-bolt" />
            <span>Aguardando eventos WebSocket...</span>
          </div>
          <div
            v-for="ev in filteredEvents"
            :key="ev.id"
            class="mfi-row"
            :class="{ 'mfi-row--selected': selectedEvent?.id === ev.id }"
            @click="selectedEvent = ev"
          >
            <span class="mfi-col-time">{{ formatTime(ev.timestamp) }}</span>
            <Tag :value="ev.event" :severity="severityFor(ev.event)" class="mfi-col-event" />
            <span class="mfi-col-instance">{{ ev.instanceId || '—' }}</span>
            <span class="mfi-col-size">{{ payloadSize(ev.data) }}</span>
          </div>
        </div>

        <!-- Payload detail -->
        <div v-if="selectedEvent" class="mfi-detail">
          <div class="mfi-detail-header">
            <span class="mfi-detail-title">{{ selectedEvent.event }}</span>
            <span class="mfi-detail-time">{{ selectedEvent.timestamp }}</span>
            <Button
              icon="pi pi-copy"
              size="small"
              text
              label="Copiar"
              @click="copyEvent()"
            />
            <Button icon="pi pi-times" size="small" text @click="selectedEvent = null" />
          </div>
          <pre class="mfi-payload">{{ JSON.stringify(selectedEvent.data, null, 2) }}</pre>
        </div>
      </div>

      <!-- Footer bar -->
      <div class="mfi-footer">
        <span>{{ filteredEvents.length }} / {{ wsStore.events.length }} eventos</span>
        <span v-if="wsStore.paused" class="mfi-paused-badge">PAUSADO</span>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.mfi-trigger {
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--p-surface-800);
  color: #fff;
  padding: 8px 14px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 600;
  z-index: 1000;
  box-shadow: 0 4px 16px rgba(0,0,0,0.3);
  transition: background 150ms ease, transform 150ms ease;
  user-select: none;
}
.mfi-trigger:hover {
  background: var(--p-surface-700);
  transform: translateY(-1px);
}
.mfi-trigger-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--p-red-400);
  flex-shrink: 0;
}
.mfi-trigger-dot--live {
  background: #25D366;
  animation: pulse 2s infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
.mfi-trigger-label { font-size: 0.8rem; }

/* Panel */
.mfi-panel {
  position: fixed;
  bottom: 64px;
  right: 16px;
  width: 780px;
  max-width: calc(100vw - 32px);
  height: 320px;
  background: var(--p-surface-900);
  border: 1px solid var(--p-surface-700);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  z-index: 999;
  box-shadow: 0 8px 40px rgba(0,0,0,0.4);
  overflow: hidden;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.8rem;
}
.mfi-panel--expanded {
  height: 520px;
}

/* Header */
.mfi-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid var(--p-surface-700);
  background: var(--p-surface-800);
  gap: 8px;
  flex-shrink: 0;
}
.mfi-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.mfi-header-right {
  display: flex;
  align-items: center;
  gap: 4px;
}
.mfi-title {
  color: #fff;
  font-weight: 700;
  font-size: 0.8125rem;
  display: flex;
  align-items: center;
  gap: 6px;
}
.mfi-status {
  font-size: 0.75rem;
  color: var(--p-red-400);
  font-weight: 600;
}
.mfi-status--live { color: #25D366; }
.mfi-filter { width: 220px; font-size: 0.75rem !important; }

/* Body */
.mfi-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}
.mfi-table-wrap {
  flex: 1;
  overflow-y: auto;
  min-width: 0;
}
.mfi-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--p-surface-400);
  gap: 8px;
  font-size: 0.8125rem;
}
.mfi-empty i { font-size: 1.5rem; }

.mfi-row {
  display: grid;
  grid-template-columns: 72px 130px 1fr 56px;
  align-items: center;
  padding: 5px 12px;
  gap: 8px;
  cursor: pointer;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  color: rgba(255,255,255,0.75);
  transition: background 100ms ease;
}
.mfi-row:hover { background: rgba(255,255,255,0.05); }
.mfi-row--selected { background: rgba(37,211,102,0.08) !important; }
.mfi-col-time { color: rgba(255,255,255,0.4); font-size: 0.75rem; }
.mfi-col-event { font-size: 0.7rem; justify-self: start; }
.mfi-col-instance { color: rgba(255,255,255,0.55); font-size: 0.75rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mfi-col-size { color: rgba(255,255,255,0.35); font-size: 0.72rem; text-align: right; }

/* Detail pane */
.mfi-detail {
  width: 300px;
  border-left: 1px solid var(--p-surface-700);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}
.mfi-detail-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-bottom: 1px solid var(--p-surface-700);
  background: var(--p-surface-800);
  flex-shrink: 0;
}
.mfi-detail-title { font-weight: 700; color: #25D366; font-size: 0.8rem; flex: 1; }
.mfi-detail-time { font-size: 0.7rem; color: rgba(255,255,255,0.35); }
.mfi-payload {
  flex: 1;
  overflow: auto;
  padding: 10px;
  margin: 0;
  color: rgba(255,255,255,0.8);
  font-size: 0.72rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-all;
}

/* Footer */
.mfi-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 12px;
  font-size: 0.72rem;
  color: rgba(255,255,255,0.35);
  border-top: 1px solid var(--p-surface-700);
  background: var(--p-surface-800);
  flex-shrink: 0;
}
.mfi-paused-badge {
  background: var(--p-orange-500);
  color: #fff;
  padding: 1px 6px;
  border-radius: 4px;
  font-weight: 700;
  font-size: 0.68rem;
}

/* Transition */
.mfi-slide-enter-active, .mfi-slide-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}
.mfi-slide-enter-from, .mfi-slide-leave-to {
  opacity: 0;
  transform: translateY(16px);
}

/* Scrollbar */
.mfi-table-wrap::-webkit-scrollbar,
.mfi-payload::-webkit-scrollbar { width: 4px; }
.mfi-table-wrap::-webkit-scrollbar-thumb,
.mfi-payload::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.12);
  border-radius: 4px;
}
</style>
